<template>
  <div class="ds-input ds-form-container">
    <label v-if="label && !$slots.label" :for="id" data-testid="ds-input-label">
      {{ label }}
    </label>
    <slot v-if="!!$slots.label" name="label" />
    <DsSkeleton
      v-if="(populating || populatingForm) && type !== 'hidden'"
      class="ds-input-skeleton"
      data-testid="ds-input-skeleton"
    />
    <div
      v-if="!(populating || populatingForm)"
      :class="[
        'ds-input-container',
        {
          'ds-with-prefix': prefix,
          'ds-with-suffix': suffix,
        },
      ]"
      data-testid="ds-input-container"
    >
      <span
        v-if="prefix"
        :class="[
          'ds-prefix',
          {
            'ds-prefix-invalid': !!error,
          },
        ]"
        data-testid="ds-input-prefix"
      >
        {{ prefix }}
      </span>
      <input
        v-if="dataMask === ''"
        :id="id"
        ref="inputRef"
        v-model="model"
        :name="name"
        :type="type"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :min="min"
        :max="max"
        :maxlength="maxlength"
        :class="[
          'ds-input-control',
          'ds-form-control',
          {
            'ds-form-control-invalid': !!error,
            'ds-input-placeholder-disabled': disabled,
            'ds-input-disable-arrows': !arrows,
          },
        ]"
        :data-testid="dataTestid"
        :step="step"
        :autofocus="autofocus"
        :disabled="disabled"
        @keypress="onKeypress"
        @focus="scrollToElement"
        @blur="onBlur"
      />
      <input
        v-else
        :id="id"
        ref="inputRef"
        v-model="model"
        v-maska
        :name="name"
        :type="type"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :min="min"
        :max="max"
        :maxlength="maxlength"
        :class="[
          'ds-input-control',
          'ds-form-control',
          {
            'ds-form-control-invalid': !!error,
            'ds-input-placeholder-disabled': disabled,
            'ds-input-disable-arrows': !arrows,
          },
        ]"
        :data-testid="dataTestid"
        :step="step"
        :autofocus="autofocus"
        :disabled="disabled"
        :data-maska="dataMask"
        :data-maska-tokens="dataMaskTokens"
        @keypress="onKeypress"
        @focus="scrollToElement"
        @blur="onBlur($event)"
      />
      <span
        v-if="suffix"
        :class="[
          'ds-suffix',
          {
            'ds-suffix-invalid': !!error,
          },
        ]"
        data-testid="ds-input-suffix"
      >
        {{ suffix }}
      </span>
    </div>
    <ErrorMessage
      :name="name"
      as="p"
      class="ds-form-control-invalid-message"
      data-testid="ds-input-validation-message"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { DsBrowser, DsScroll } from "../../helpers";
import { DsEmitBase, DsPropsBase, useDsInputBase } from "../form/DsInputBase";
import { vMaska } from "maska/vue";
import { ErrorMessage } from "vee-validate";

import DsSkeleton from "../skeleton/DsSkeleton.vue";

defineOptions({
  name: "DsInput",
});

const emit = defineEmits([...DsEmitBase, "focused", "preventedKey", "blur"]);

const props = defineProps({
  ...DsPropsBase,
  modelValue: {
    type: [String, Number],
    default: "",
  },
  prefix: {
    type: String,
    default: "",
  },
  suffix: {
    type: String,
    default: "",
  },
  arrows: {
    type: Boolean,
    default: true,
  },
  min: {
    type: String,
    required: false,
  },
  max: {
    type: String,
    required: false,
  },
  maxlength: {
    type: String,
    required: false,
  },
  step: {
    type: String,
    required: false,
  },
  scrollOnFocus: {
    type: Boolean,
    default: false,
  },
  dataMask: {
    type: String,
    default: "",
  },
  dataMaskTokens: {
    type: String,
    default: "",
  },
});

const inputRef = ref<HTMLInputElement | null>(null);
const { model, populatingForm, error } = useDsInputBase<string | number>(props);

const isNumberType = computed((): boolean => {
  return typeof props.modelValue === "number" || props.type === "number";
});

function isNumber(evt: KeyboardEvent): void {
  const keysAllowed: string[] = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    ".",
    "Enter",
  ];
  const keyPressed: string = evt.key;

  if (!keysAllowed.includes(keyPressed)) {
    evt.preventDefault();
    emit("preventedKey", keyPressed);
  }
}

function onKeypress(event: KeyboardEvent): void {
  if (isNumberType.value) {
    isNumber(event);
  }
}

const focus = (): void => {
  inputRef.value?.focus();
  emit("focused");
};

const scrollToElement = (): void => {
  if (DsBrowser.isMobile() && props.scrollOnFocus) {
    DsScroll.to(props.id as string);
  }
};

function onBlur(event: Event) {
  emit("blur", event);
}

defineExpose({
  focus,
  scrollToElement,
});
</script>

<style scoped lang="scss">
.ds-input {
  &-container {
    @apply flex;
  }

  &-skeleton {
    @apply w-full h-11;
  }

  &-control {
    @apply text-base border rounded-lg w-full py-[.5625rem] px-4;
  }

  &-control:not(.ds-form-control-invalid):not(:focus) {
    @apply bg-gray-50 border-gray-300;
  }

  &-placeholder-disabled {
    @apply placeholder:text-gray-400 placeholder-opacity-10;
  }

  &-disable-arrows::-webkit-inner-spin-button,
  &-disable-arrows::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
